import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, Clock, MapPin } from 'lucide-react';
import { currentPopup } from '../data/popups';
const ease = [0.23, 1, 0.32, 1] as const;
export function Hero() {
  const reduceMotion = useReducedMotion();
  const p = currentPopup;
  return <section aria-labelledby="hero-heading" className="relative bg-foodCream border-b-4 border-foodMaroon overflow-hidden" data-id="element-82" data-mp="4mi4le603ug2">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center" data-id="element-83" data-mp="127efjh2kpvf1">
        {/* Copy — Pavilion voice, neutral espresso so the vendor's colors lead */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease
      }} className="lg:col-span-6 order-2 lg:order-1" data-id="element-84" data-mp="sd4d16jx3gql">
          <span className="inline-block -skew-x-12 bg-foodMaroon border-2 border-foodMaroon px-4 py-1.5 mb-6" data-id="element-85" data-mp="382p73hcojln9">
            <span className="inline-block skew-x-12 text-foodCream text-sm font-bold uppercase tracking-wide" data-id="element-86" data-mp="3j7zh7oa6s2jb">
              Now popping up at the Pavilion
            </span>
          </span>

          <h1 id="hero-heading" className="text-8xl sm:text-9xl lg:text-[10rem] uppercase leading-[0.82]" data-id="element-87" data-mp="39al869qleg11">
            {p.name}
          </h1>
          <p className="font-display text-3xl sm:text-4xl font-bold uppercase mt-3" data-id="element-88" data-mp="3eb91j22an8yf">
            {p.tagline}
          </p>
          <p className="text-lg font-semibold text-foodMaroon/70 mt-1" data-id="element-89" data-mp="3599hs1zw9qrs">
            with {p.chef}
          </p>

          <p className="text-lg text-foodMaroon/85 mt-6 max-w-lg" data-id="element-90" data-mp="2qkmt39odnmnw">{p.intro}</p>

          <ul className="flex flex-wrap gap-3 mt-8" aria-label="Pop-up dates" data-id="element-91" data-mp="2viokchvyisag">
            {p.days.map(d => <li key={d.date} className="bg-foodCard border-2 border-foodMaroon px-4 py-2 text-center min-w-[5.5rem]" data-id="element-92" data-mp="2hix0i0irehoj">
                <span className="block text-xs font-bold uppercase text-foodTaupe" data-id="element-93" data-mp="s21h48i0i7ye">
                  {d.weekday}
                </span>
                <span className="block font-display text-2xl font-extrabold uppercase leading-none mt-0.5" data-id="element-94" data-mp="nb1o3ec0ka16">
                  {d.date}
                </span>
              </li>)}
          </ul>

          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-5 font-bold" data-id="element-95" data-mp="de6munocvvj7">
            <span className="inline-flex items-center gap-2" data-id="element-96" data-mp="1b1ffr3qos6v8">
              <Clock size={18} aria-hidden="true" data-id="element-97" data-mp="1ayt7f1vz1nby" />
              {p.hours}
            </span>
            <span className="inline-flex items-center gap-2" data-id="element-98" data-mp="15zq2rijwlj4i">
              <MapPin size={18} aria-hidden="true" data-id="element-99" data-mp="111om149y9vw7" />
              India Basin Waterfront Park
            </span>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 mt-10" data-id="element-100" data-mp="x0uy4awich3b">
            <a href={p.menuUrl} target="_blank" rel="noopener noreferrer" className="inline-flex justify-center items-center gap-2 bg-foodMaroon text-foodCream px-8 py-4 font-bold text-lg border-2 border-foodMaroon shadow-[4px_4px_0px_0px_#8A7A6D] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_#8A7A6D] transition-[transform,box-shadow] duration-150 ease-out" data-id="element-101" data-mp="3lu0454qihb1b">
              See the Menu
              <ArrowRight size={20} aria-hidden="true" data-id="element-102" data-mp="2b0ze8vblj9pb" />
            </a>
          </div>
        </motion.div>

        {/* Vendor stage — a white safe area where the vendor's own brand lives */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.3,
        ease
      }} className="lg:col-span-6 order-1 lg:order-2 relative w-full max-w-xl mx-auto" data-id="element-103" data-mp="2kydsqpdyloez">
          <div className="bg-white border-4 border-foodMaroon shadow-[12px_12px_0px_0px_#2A1B14] aspect-square overflow-hidden" data-id="element-104" data-mp="2txhclvkya7uj">
            <video src={p.video} autoPlay loop muted playsInline aria-label={`${p.name} ${p.tagline} animation`} className="w-full h-full object-contain" data-id="element-105" data-mp="zlapbyes80lv" />
          </div>

          <motion.div animate={reduceMotion ? undefined : {
          scale: [1, 1.06, 1]
        }} transition={{
          duration: 1.6,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute -top-6 -right-3 sm:-right-8 w-32 h-32 rounded-full bg-foodCream border-4 border-foodMaroon flex flex-col items-center justify-center text-center shadow-[4px_4px_0px_0px_#2A1B14]" data-id="element-106" data-mp="e9jypxg05fth">
            <span className="font-display text-2xl font-extrabold uppercase leading-[0.9]" data-id="element-107" data-mp="3rg3k7oeuks4p">
              This
              <br data-id="element-108" data-mp="3mpn7k6iupaje" />
              Weekend
            </span>
            <span className="text-xs font-bold uppercase tracking-wide mt-1.5" data-id="element-109" data-mp="3mo2dsj4d1vml">
              Oct 3 &amp; 4
            </span>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}